import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Button from '../Button/Button';
import { ReactComponent as InfoGttIcon } from '../../assets/images/priceAlerts/info-gtt.svg';
import { pxToRem, mediaBelow } from '../../assets/styles/helper';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { authScopeStringGetHelper } from '../../util/helpers/authScopeHelpers';
import { JWT_TOKEN } from '../../constants/localStorage';
import {
  getCurrentAccountCode,
  getCurrentAccountUid,
} from '../../store/selectors/accountSelectors';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const Card = styled.div`
  position: relative;
  background: ${themeColors.colorWhite};
  border-radius: ${variables.borderRadius.borderRadiusLg};
  padding: ${pxToRem(24)};
  width: 100%;
  max-width: ${pxToRem(420)};
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);

  ${mediaBelow(variables.breakpoints.bpMd)} {
    max-width: calc(100% - ${pxToRem(32)});
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${pxToRem(16)};
`;

const Icon = styled(InfoGttIcon)`
  width: 35.75px;
  height: 35.75px;

  path {
    fill: ${themeColors.colorTextSecondary};
  }
`;

const Title = styled.p`
  margin: 0;
  font-family: ${variables.family.goldSilver};
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0px;
  vertical-align: middle;
  color: ${themeColors.colorTextPrimary};
`;

const Body = styled.p`
  margin: 0;
  font-family: ${variables.family.goldSilver};
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
  letter-spacing: 0%;
  text-align: center;
  vertical-align: middle;
  color: ${themeColors.colorTextSecondary};
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: ${pxToRem(12)};

  > button {
    flex: 1;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    flex-direction: column;

    > button {
      flex: none;
      width: 100%;
      height: 44px;
    }
  }
`;

const ModalButton = styled(Button)`
  font-family: ${variables.family.goldSilverHeaders};
  font-weight: 600;
  font-size: 16px;
  line-height: 130%;
  letter-spacing: 0%;
  vertical-align: middle;
`;

const ModalOutlinedButton = styled(ModalButton)`
  color: ${themeColors.colorButtonOutlinedText};
`;

const SupportLink = styled.a`
  font-family: ${variables.family.goldSilver};
  font-size: 14px;
  line-height: 140%;
  color: ${themeColors.colorPrimary};
  text-decoration: underline;
`;

const TriggerOrderVerificationModal = ({ isOpen, close, variant }) => {
  const { t } = useTranslation();
  const { RegistrationUrlV2 } = useSelector(selectSettings);
  const token = authScopeStringGetHelper(JWT_TOKEN);
  const accountUid = useSelector(getCurrentAccountUid);
  const accountCode = useSelector(getCurrentAccountCode);
  const phone = t('support.customerServicePhoneGS');
  const phoneDigits = phone.replace(/[^\d+]/g, '');

  useEffect(() => {
    if (!isOpen) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const goToRegistrationPage = () => {
    window.location.replace(
      `${RegistrationUrlV2}/choose-application?accountCode=${accountCode}&accountUid=${accountUid}&token=${token}`,
    );
  };

  const isDeliveryOnly = variant === 'deliveryOnly';

  return ReactDOM.createPortal(
    <Overlay onClick={close}>
      <Card
        onClick={(e) => e.stopPropagation()}
        data-cy="gtt-trigger-order-verification"
      >
        <Content>
          <Icon />
          <Title>
            {isDeliveryOnly
              ? t('gtt.triggerOrders.verification.title')
              : t('gtt.triggerOrders.verification.pendingTitle')}
          </Title>
          <Body>
            {isDeliveryOnly
              ? t('gtt.triggerOrders.verification.deliveryOnlyBody')
              : t('gtt.triggerOrders.verification.pendingBody')}
          </Body>
          {!isDeliveryOnly && (
            <SupportLink
              href={`tel:${phoneDigits}`}
              data-cy="gtt-verification-support"
            >
              {t('gtt.readOnly.supportLink', { phone })}
            </SupportLink>
          )}
          <ButtonsWrapper>
            {isDeliveryOnly && (
              <ModalButton
                variant="primary"
                height="44px"
                textTransform="none"
                onClick={goToRegistrationPage}
                data-cy="gtt-verify-account"
              >
                {t('gtt.triggerOrders.verification.verifyAccount')}
              </ModalButton>
            )}
            <ModalOutlinedButton
              variant="primaryOutlined"
              height="44px"
              textTransform="none"
              onClick={close}
              data-cy={
                isDeliveryOnly ? 'gtt-do-it-later' : 'gtt-verification-close'
              }
            >
              {isDeliveryOnly
                ? t('gtt.triggerOrders.verification.doItLater')
                : t('gtt.triggerOrders.verification.pendingClose')}
            </ModalOutlinedButton>
          </ButtonsWrapper>
        </Content>
      </Card>
    </Overlay>,
    document.body,
  );
};

TriggerOrderVerificationModal.propTypes = {
  isOpen: PropTypes.bool,
  close: PropTypes.func,
  variant: PropTypes.oneOf(['deliveryOnly', 'pending']),
};

export default TriggerOrderVerificationModal;
